import { useCallback } from 'react';
import type { SortConfig, SortDirection } from '../types';

export interface UseSortingOptions {
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
  columns?: any[]; // 🎯 Añadir columnas para verificar sortable
}

export const useSorting = ({ sorting, onSortingChange, columns = [] }: UseSortingOptions) => {
  const handleSort = useCallback((field: string) => {
    if (!onSortingChange) return;

    // 🎯 Verificar si la columna es sortable
    const column = columns.find(col => col.accessorKey === field);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';
    
    // Si es el mismo campo, ciclar entre asc -> desc -> null
    if (sorting?.field === field) {
      if (sorting.direction === 'asc') {
        newDirection = 'desc';
      } else if (sorting.direction === 'desc') {
        newDirection = null;
      }
    }

    // Si newDirection es null, limpiar el sorting
    if (newDirection === null) {
      onSortingChange(null);
    } else {
      onSortingChange({ field, direction: newDirection });
    }
  }, [sorting, onSortingChange, columns]);

  const getSortIcon = useCallback((field: string): string => {
    if (sorting?.field !== field) return '';
    
    switch (sorting.direction) {
      case 'asc': return '↑';
      case 'desc': return '↓';
      default: return '';
    }
  }, [sorting]);

  const isSortable = useCallback((field?: string) => {
    if (!field || !onSortingChange) return false;
    
    // 🎯 Verificar si la columna tiene sortable: true
    const column = columns.find(col => col.accessorKey === field);
    return column?.sortable === true;
  }, [onSortingChange, columns]);

  return {
    handleSort,
    getSortIcon,
    isSortable,
    currentSort: sorting
  };
}; 